import DOMPurify from 'dompurify';

const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'h4', 'blockquote', 'ul', 'ol', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input'],
  FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover', 'onmouseout']
};

const sanitizeHtml = (html = '') => DOMPurify.sanitize(html, SANITIZE_OPTIONS);

const decodeEntities = (text = '') =>
  text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

export const generatePlainTextExcerpt = (content, targetLength = 200, maxLines = 3) => {
  if (!content || typeof content !== 'string') return '';

  let text = sanitizeHtml(content)
    .replace(/<br\s*\/?\>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/(li|ul|ol)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\r\n|\r/g, '\n');

  text = decodeEntities(text)
    .replace(/[\t ]+/g, ' ')
    .replace(/\n\s+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!text) return '';

  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  if (!lines.length) return '';

  const excerptLines = [];
  let charCount = 0;
  const charLimit = Math.max(targetLength, 0);

  for (const line of lines) {
    if (excerptLines.length >= maxLines || (charCount >= charLimit && excerptLines.length)) {
      break;
    }

    let segment = line;
    const remaining = charLimit - charCount;

    if (charLimit > 0 && remaining > 0 && segment.length > remaining) {
      segment = segment.substring(0, remaining);
      const lastSpace = segment.lastIndexOf(' ');
      if (lastSpace > remaining * 0.6) {
        segment = segment.substring(0, lastSpace);
      }
    }

    if (!segment) continue;

    excerptLines.push(segment);
    charCount += segment.length;
  }

  let excerpt = excerptLines.join('\n').trim();
  const truncated = excerpt.length < text.length;

  if (truncated && excerpt) {
    excerpt = excerpt.replace(/[\.…]+$/, '').trim();
    excerpt += '…';
  }

  return excerpt;
};

const cloneWithLimit = (node, remaining) => {
  const ELEMENT_NODE = 1;
  const TEXT_NODE = 3;

  if (!node || remaining <= 0) {
    return { node: null, remaining, truncated: remaining <= 0 };
  }

  if (node.nodeType === TEXT_NODE) {
    const text = node.textContent || '';
    if (!text) return { node: null, remaining, truncated: false };

    if (text.length <= remaining) {
      return { node: document.createTextNode(text), remaining: remaining - text.length, truncated: false };
    }

    return { node: document.createTextNode(text.substring(0, remaining)), remaining: 0, truncated: true };
  }

  if (node.nodeType === ELEMENT_NODE) {
    const clone = node.cloneNode(false);
    if (node.tagName === 'BR') {
      return { node: clone, remaining, truncated: false };
    }

    let truncated = false;
    let currentRemaining = remaining;

    node.childNodes.forEach((child) => {
      if (currentRemaining <= 0) {
        truncated = true;
        return;
      }
      const result = cloneWithLimit(child, currentRemaining);
      if (result.node) {
        clone.appendChild(result.node);
      }
      currentRemaining = result.remaining;
      if (result.truncated) {
        truncated = true;
      }
    });

    return { node: clone, remaining: currentRemaining, truncated };
  }

  return { node: null, remaining, truncated: false };
};

const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

export const generateFormattedExcerpt = (content, targetLength = 240) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  const sanitized = sanitizeHtml(content);
  if (!sanitized) return '';

  if (!hasDOM) {
    return generatePlainTextExcerpt(sanitized, targetLength).replace(/\n/g, '<br />');
  }

  const temp = document.createElement('div');
  temp.innerHTML = sanitized;

  const fragment = document.createDocumentFragment();
  let remaining = Math.max(targetLength, 0);
  let truncated = false;

  Array.from(temp.childNodes).forEach((child) => {
    if (remaining <= 0) {
      truncated = true;
      return;
    }
    const result = cloneWithLimit(child, remaining);
    if (result.node) {
      fragment.appendChild(result.node);
    }
    remaining = result.remaining;
    if (result.truncated) {
      truncated = true;
    }
  });

  const container = document.createElement('div');
  container.appendChild(fragment);
  let html = container.innerHTML.trim();

  if (truncated && html) {
    html = html.replace(/(&nbsp;|\s|<br\s*\/?\>)+$/gi, '');
    html += '…';
  }

  return html || sanitized;
};

export const generateExcerpt = generatePlainTextExcerpt;
